import React, { useEffect, useState } from "react";
import API from "../api/axiosConfig";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "react-toastify";
import {
  BarChart3, PackageSearch, ShoppingBag, Pencil, ChevronRight,
  Clock, TrendingUp, ExternalLink,
  RefreshCw, CheckCircle2, X as XIcon,
} from "lucide-react";
import EditCompanyModal from "../components/modals/EditCompanyModal";
import QRWithLogo from "../components/QRWithLogo";

const STATUS_MAP = {
  pendiente:  { label: "Pendiente",  cls: "bg-yellow-100 text-yellow-700" },
  confirmado: { label: "Confirmado", cls: "bg-blue-100 text-blue-700" },
  asignado:   { label: "Asignado",   cls: "bg-violet-100 text-violet-700" },
  en_camino:  { label: "En camino",  cls: "bg-indigo-100 text-indigo-700" },
  entregado:  { label: "Entregado",  cls: "bg-green-100 text-green-700" },
  cancelado:  { label: "Cancelado",  cls: "bg-red-100 text-red-600" },
};

const ORDER_FILTERS = [
  { key: "activos",    label: "Activos" },
  { key: "pendiente",  label: "Pendientes" },
  { key: "entregado",  label: "Entregados" },
  { key: "todos",      label: "Todos" },
];

/* ─── Setup checklist ─── */
function SetupChecklist({ companyId, hasSede, hasSeccion, hasProduct, onDismiss }) {
  const navigate = useNavigate();

  const steps = [
    {
      label: "Registro completado",
      desc:  "Tu cuenta y empresa están creadas",
      done:  true,
    },
    {
      label: "Agrega tu primera sede",
      desc:  "Los clientes necesitan saber dónde encontrarte",
      done:  hasSede,
      cta:   "Agregar sede",
      to:    "/company/sedes",
    },
    {
      label: "Crea una sección de catálogo",
      desc:  "Organiza tus productos en categorías",
      done:  hasSeccion,
      cta:   "Crear sección",
      to:    "/company/seccions",
    },
    {
      label: "Agrega tu primer producto",
      desc:  "Los clientes podrán ver y pedir tus productos",
      done:  hasProduct,
      cta:   "Agregar producto",
      to:    "/company/posts",
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const allDone   = doneCount === steps.length;
  if (allDone) return null;

  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-4 sm:px-5 pt-4 pb-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-bold text-gray-800">🚀 Completa la configuración de tu tienda</p>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-400">{doneCount}/{steps.length}</span>
            <button onClick={onDismiss} className="text-gray-300 hover:text-gray-500 transition" title="Ocultar">
              <XIcon size={14} />
            </button>
          </div>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5">
          <div
            className="bg-supernova-500 h-1.5 rounded-full transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Pasos */}
      <div className="border-t border-gray-50 divide-y divide-gray-50">
        {steps.map((step, i) => (
          <div key={i} className={`flex items-center gap-3 px-4 sm:px-5 py-3 ${step.done ? "opacity-50" : ""}`}>
            {/* Icono */}
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
              step.done ? "bg-green-100" : "bg-supernova-50"
            }`}>
              {step.done
                ? <CheckCircle2 size={15} className="text-green-500" />
                : <span className="text-xs font-bold text-supernova-600">{i + 1}</span>
              }
            </div>

            {/* Texto */}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold leading-tight ${step.done ? "line-through text-gray-400" : "text-gray-800"}`}>
                {step.label}
              </p>
              {!step.done && (
                <p className="text-[11px] text-gray-400 leading-tight mt-0.5">{step.desc}</p>
              )}
            </div>

            {/* CTA */}
            {!step.done && step.to && (
              <button
                onClick={() => navigate(step.to)}
                className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-supernova-600 hover:text-supernova-700 whitespace-nowrap transition"
              >
                {step.cta}
                <ChevronRight size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Tooltip gráfica ─── */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg px-3 py-2 text-xs">
      <p className="text-gray-500 mb-0.5">{label}</p>
      <p className="font-bold text-gray-800">{payload[0].value} visitas</p>
    </div>
  );
}

/* ─── Badge de estado ─── */
function StatusBadge({ status }) {
  const s = STATUS_MAP[status] ?? { label: status, cls: "bg-gray-100 text-gray-600" };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${s.cls}`}>
      {s.label}
    </span>
  );
}

/* ─── Formateador de fecha corta ─── */
function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

/* ══════════════════════════════════════════════════════════════════ */
export default function CompanyDashboard() {
  const navigate = useNavigate();
  const SHARE_URL = (process.env.REACT_APP_URL || "http://localhost:3000").replace(/\/$/, "");

  const [company,        setCompany]        = useState(null);
  const [metrics,        setMetrics]        = useState(null);
  const [pendingCount,   setPendingCount]   = useState(0);
  const [orders,         setOrders]         = useState([]);
  const [ordersLoading,  setOrdersLoading]  = useState(false);
  const [orderFilter,    setOrderFilter]    = useState("activos");
  const [openEdit,       setOpenEdit]       = useState(false);
  const [loading,        setLoading]        = useState(true);

  /* Setup checklist */
  const [hasSede,         setHasSede]         = useState(false);
  const [hasSeccion,      setHasSeccion]      = useState(false);
  const [setupDismissed,  setSetupDismissed]  = useState(false);

  /* ── Carga empresa ── */
  useEffect(() => {
    const load = async () => {
      try {
        const res  = await API.get("/auth/me");
        const user = res.data?.data?.user ?? res.data?.user ?? res.data;
        const companyId = user?.companyId ?? user?.company?.id;
        if (!companyId) { toast.error("No tienes una empresa asociada"); navigate("/"); return; }
        // Siempre carga desde el endpoint completo para tener todos los campos (hasOwnDelivery, deliveryFee, etc.)
        const cr = await API.get(`/companies/by-id/${companyId}`);
        const comp = cr.data?.data?.company ?? cr.data?.data ?? cr.data;
        if (!comp) { toast.error("No tienes una empresa asociada"); navigate("/"); return; }
        setCompany(comp);
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem("user", JSON.stringify({ ...stored, company: comp }));
      } catch {
        toast.error("Error cargando empresa"); navigate("/");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  /* ── Setup checklist: sedes + secciones ── */
  useEffect(() => {
    if (!company?.id) return;
    const key = `qk_setup_dismissed_${company.id}`;
    if (localStorage.getItem(key) === "true") { setSetupDismissed(true); return; }
    Promise.all([
      API.get(`/sedes/company/${company.id}`).catch(() => null),
      API.get(`/secciones/company/${company.id}`).catch(() => null),
    ]).then(([sedesRes, seccionsRes]) => {
      const sedes    = sedesRes?.data?.data?.sedes    ?? sedesRes?.data?.sedes    ?? sedesRes?.data;
      const seccions = seccionsRes?.data?.data?.secciones ?? seccionsRes?.data?.secciones ?? seccionsRes?.data;
      setHasSede(Array.isArray(sedes)    && sedes.length    > 0);
      setHasSeccion(Array.isArray(seccions) && seccions.length > 0);
    });
  }, [company?.id]);

  const handleDismissSetup = () => {
    localStorage.setItem(`qk_setup_dismissed_${company.id}`, "true");
    setSetupDismissed(true);
  };

  /* ── Pedidos pendientes (polling) ── */
  useEffect(() => {
    if (!company?.id) return;
    const poll = async () => {
      try {
        const r = await API.get(`/orders/company/${company.id}/pending-count`);
        setPendingCount(r.data?.data?.count ?? r.data?.count ?? 0);
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 30000);
    return () => clearInterval(iv);
  }, [company?.id]);

  /* ── Métricas ── */
  useEffect(() => {
    if (!company?.id) return;
    API.get(`/metrics/company/${company.id}`)
      .then((r) => {
        const raw = r.data?.data ?? r.data;
        const visitSeries = raw?.orderStats?.visitSeries ?? [];
        const last7Days = visitSeries.slice(-7).map((item) => ({
          day: new Date(item.date + "T00:00:00").toLocaleDateString("es-CO", { weekday: "short" }),
          visits: item.visits,
        }));
        setMetrics({
          visitsToday:  raw?.summary?.visitsToday  ?? 0,
          totalVisits:  raw?.summary?.totalVisits  ?? 0,
          productCount: raw?.summary?.productCount ?? 0,
          last7Days,
        });
      })
      .catch((err) => {
        console.error("[Métricas] error:", err?.response?.status, err?.response?.data ?? err?.message);
        toast.error("Error cargando métricas");
      });
  }, [company?.id]);

  /* ── Carga de pedidos ── */
  const loadOrders = async (companyId, filter) => {
    setOrdersLoading(true);
    try {
      const params = new URLSearchParams({ limit: 10, page: 1 });
      if (filter === "activos") {
        params.set("status", "pendiente,confirmado,asignado,en_camino");
      } else if (filter !== "todos") {
        params.set("status", filter);
      }
      const r = await API.get(`/orders/company/${companyId}?${params}`);
      const list = r.data?.data?.data ?? r.data?.data ?? r.data ?? [];
      setOrders(Array.isArray(list) ? list : []);
    } catch {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    if (!company?.id) return;
    loadOrders(company.id, orderFilter);
  }, [company?.id, orderFilter]);

  /* ── Loading screen ── */
  if (loading || !company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-supernova-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-400">Cargando panel...</p>
        </div>
      </div>
    );
  }

  const isBlocked   = company.status !== "Aprobado" && company.status !== "Activo";
  const statusLabel = isBlocked ? "En revisión" : "Activo";

  const kpis = [
    { label: "Visitas hoy",   value: metrics?.visitsToday  ?? "—", icon: BarChart3,     color: "text-blue-500",    bg: "bg-blue-50" },
    { label: "Total visitas", value: metrics?.totalVisits  ?? "—", icon: TrendingUp,    color: "text-violet-500",  bg: "bg-violet-50" },
    { label: "Productos",     value: metrics?.productCount ?? "—", icon: PackageSearch, color: "text-emerald-500", bg: "bg-emerald-50" },
    { label: "Pendientes",    value: pendingCount,                  icon: Clock,         color: "text-orange-500",  bg: "bg-orange-50" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ══ PAGE HEADER ══ */}
      <div className="bg-white border-b border-gray-100 px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <ShoppingBag size={18} className="text-supernova-500 shrink-0" />
          <h1 className="text-base font-black text-gray-800 truncate">Dashboard</h1>
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
            isBlocked ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isBlocked ? "bg-yellow-500" : "bg-green-500"}`} />
            {statusLabel}
          </span>
        </div>
        <button
          onClick={() => !isBlocked && setOpenEdit(true)}
          disabled={isBlocked}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition ${
            isBlocked ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-gray-200 text-gray-600 hover:bg-gray-50 active:scale-95"
          }`}
        >
          <Pencil size={13} /> Editar empresa
        </button>
      </div>

      {/* ══ CONTENT ══ */}
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4">

        {isBlocked && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs sm:text-sm px-4 py-3 rounded-xl">
            Tu empresa está <strong>en revisión</strong>. Las funciones estarán disponibles una vez aprobada.
          </div>
        )}

        {!isBlocked && !setupDismissed && (
          <SetupChecklist
            companyId={company.id}
            hasSede={hasSede}
            hasSeccion={hasSeccion}
            hasProduct={(metrics?.productCount ?? 0) > 0}
            onDismiss={handleDismissSetup}
          />
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {kpis.map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-100 flex items-center gap-2.5 sm:gap-3">
              <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
                <Icon size={16} className={color} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-gray-400 leading-tight truncate">{label}</p>
                <p className="text-lg sm:text-xl font-bold text-gray-800 leading-tight">{value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Gráfica + QR */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100">
            <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-3 sm:mb-4">Visitas — últimos 7 días</p>
            {metrics?.last7Days?.length ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={metrics.last7Days} barSize={22} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={24} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f9fafb", radius: 6 }} />
                  <Bar dataKey="visits" fill="#f59e0b" radius={[5, 5, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-40 flex flex-col items-center justify-center gap-1 text-gray-300">
                <BarChart3 size={32} />
                <p className="text-xs">Sin datos aún</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100 flex flex-col items-center justify-center gap-3">
            <p className="text-xs sm:text-sm font-semibold text-gray-700 self-stretch">Catálogo público</p>
            <QRWithLogo value={`${SHARE_URL}/company/${company.id}`} size={120} />
            <p className="text-[10px] sm:text-xs text-gray-400 text-center leading-snug">Comparte este QR con tus clientes</p>
          </div>
        </div>

        {/* ══ PEDIDOS ══ */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

          {/* Header pedidos */}
          <div className="flex items-center justify-between px-4 sm:px-5 pt-4 pb-3 border-b border-gray-50">
            <div className="flex items-center gap-2">
              <ShoppingBag size={15} className="text-supernova-500" />
              <p className="text-sm font-bold text-gray-800">Pedidos</p>
              {pendingCount > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {pendingCount} pendientes
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadOrders(company.id, orderFilter)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 transition"
                title="Actualizar"
              >
                <RefreshCw size={13} className={ordersLoading ? "animate-spin" : ""} />
              </button>
              <button
                onClick={() => navigate("/company/orders")}
                className="flex items-center gap-1 text-xs font-semibold text-supernova-600 hover:text-supernova-700 transition"
              >
                Ver todos
                <ExternalLink size={11} />
              </button>
            </div>
          </div>

          {/* Filtros */}
          <div className="flex gap-1 px-4 sm:px-5 py-2.5 overflow-x-auto">
            {ORDER_FILTERS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setOrderFilter(key)}
                className={`flex-shrink-0 text-xs font-semibold px-3 py-1 rounded-full transition ${
                  orderFilter === key
                    ? "bg-supernova-500 text-white"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Lista de pedidos */}
          <div className="divide-y divide-gray-50">
            {ordersLoading ? (
              <div className="flex items-center justify-center py-10 gap-2 text-gray-400">
                <div className="w-5 h-5 border-2 border-supernova-300 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Cargando pedidos...</span>
              </div>
            ) : orders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-1 text-gray-300">
                <ShoppingBag size={28} />
                <p className="text-sm">No hay pedidos en este filtro</p>
              </div>
            ) : (
              orders.map((order) => (
                <button
                  key={order.id}
                  onClick={() => navigate("/company/orders")}
                  className="w-full flex items-center gap-3 px-4 sm:px-5 py-3 hover:bg-gray-50 transition text-left"
                >
                  {/* Número de orden */}
                  <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-gray-500">
                      #{order.id?.slice(-4).toUpperCase()}
                    </span>
                  </div>

                  {/* Info principal */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {order.user?.name ?? "Cliente"}
                      </p>
                      <StatusBadge status={order.status} />
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-[11px] text-gray-400">{fmtDate(order.createdAt)}</p>
                      {order.items?.length > 0 && (
                        <p className="text-[11px] text-gray-400">
                          · {order.items.length} {order.items.length === 1 ? "ítem" : "ítems"}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Total */}
                  <div className="flex-shrink-0 text-right">
                    <p className="text-sm font-bold text-gray-800">
                      ${Number(order.total ?? 0).toLocaleString("es-CO")}
                    </p>
                    <ChevronRight size={13} className="text-gray-300 ml-auto mt-0.5" />
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          {orders.length > 0 && (
            <div className="px-4 sm:px-5 py-3 border-t border-gray-50">
              <button
                onClick={() => navigate("/company/orders")}
                className="w-full text-center text-xs font-semibold text-supernova-600 hover:text-supernova-700 transition py-1"
              >
                Ver todos los pedidos →
              </button>
            </div>
          )}
        </div>

      </div>

      {openEdit && (
        <EditCompanyModal
          company={company}
          onClose={() => setOpenEdit(false)}
          onUpdated={(updated) => {
            setCompany(updated);
            const stored = JSON.parse(localStorage.getItem("user") || "{}");
            localStorage.setItem("user", JSON.stringify({ ...stored, company: updated }));
          }}
        />
      )}
    </div>
  );
}
